import jsPDF from "jspdf";
import logo from "../assets/icons/qvh-logo.svg";
import { RECIPES, ZUSATZ_INFO, getSubVariantKey } from "./rezeptvorschlagUtils";

function normalizeText(text) {
  return typeof text === "string" ? text.normalize("NFC") : text;
}

function clean(obj) {
  if (!obj) return obj;
  if (typeof obj === "string") return obj.normalize("NFC");
  if (Array.isArray(obj)) return obj.map((x) => clean(x));
  if (typeof obj === "object") {
    const out = {};
    for (const k in obj) out[k] = clean(obj[k]);
    return out;
  }
  return obj;
}

export async function generateRezeptvorschlagPdf(payloadRaw) {
  const payload = clean(payloadRaw);
  const general          = payload.general_data         || {};
  const fussstatus       = payload.foot_status          || {};
  const fussstatusGrafik = payload.foot_status_graphic  || {};
  const categorization   = payload.categorization       || {};

  const fussAuswahl   = general.fussAuswahl;
  const showLeftFoot  = fussAuswahl !== "rechts";
  const showRightFoot = fussAuswahl !== "links";
  const showBothFeet  = showLeftFoot && showRightFoot;

  const linksTyp  = categorization.linksRisikotyp  || "Kein Typ";
  const rechtsTyp = categorization.rechtsRisikotyp || "Kein Typ";

  const subKeyLinks    = showLeftFoot  ? getSubVariantKey(linksTyp,  fussstatus, fussstatusGrafik, "links")  : null;
  const subKeyRechts   = showRightFoot ? getSubVariantKey(rechtsTyp, fussstatus, fussstatusGrafik, "rechts") : null;
  const sameSubVariant = showBothFeet && subKeyLinks === subKeyRechts;

  const doc        = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth  = 210;
  const pageHeight = 297;
  const margin     = 15;
  const footerH    = 18;

  let y = 15;
  const today = new Date().toLocaleDateString("de-DE");

  // ── Page-break guard ─────────────────────────────────────────────────
  const checkY = (needed = 10) => {
    if (y + needed > pageHeight - footerH) {
      doc.addPage();
      y = 15;
    }
  };

  // ── Header ──────────────────────────────────────────────────────────
  const logoPng = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 414; canvas.height = 177;
      canvas.getContext("2d").drawImage(img, 0, 0, 414, 177);
      resolve(canvas.toDataURL("image/png"));
    };
    img.src = logo;
  });
  const imgWidth  = 40;
  const imgHeight = (177 / 414) * imgWidth;
  doc.addImage(logoPng, "PNG", (pageWidth - imgWidth) / 2, y, imgWidth, imgHeight);
  doc.setFontSize(8);
  doc.setTextColor(80);
  doc.text(`Datum: ${today}`, pageWidth - margin, y + 4, { align: "right" });
  y += imgHeight + 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text("Rezeptvorschlag", pageWidth / 2, y, { align: "center" });
  y += 12;

  // ── Section title ────────────────────────────────────────────────────
  const sectionTitle = (text) => {
    checkY(12);
    doc.setFillColor(235, 235, 235);
    doc.rect(margin - 2, y - 5, pageWidth - margin * 2 + 4, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(normalizeText(text), margin, y + 1);
    doc.setFontSize(9);
    y += 8;
  };

  // ── Patient field ────────────────────────────────────────────────────
  const field = (label, value) => {
    if (!value || value === "—") return;
    checkY(6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`${label}:`, margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(normalizeText(String(value)), margin + 45, y);
    y += 6;
  };

  // ── Patientendaten ───────────────────────────────────────────────────
  sectionTitle("Patientendaten");
  field("Vorname",      general.vorname      || "—");
  field("Name",         general.name         || "—");
  field("Geburtsdatum", general.geburtsdatum || "—");
  y += 4;

  // ── Rezeptvorschlag ──────────────────────────────────────────────────
  sectionTitle("Rezeptvorschlag");

  const drawZusatz = (subKey) => {
    const info = ZUSATZ_INFO[subKey];
    if (!info) return;

    checkY(10);
    y += 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text("Zusätze:", margin, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    info.zusaetze.forEach((item) => {
      const lines = doc.splitTextToSize(`•  ${normalizeText(item)}`, pageWidth - margin * 2 - 4);
      checkY(lines.length * 4.5);
      doc.text(lines, margin + 2, y);
      y += lines.length * 4.5;
    });

    checkY(12);
    y += 3;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Alternative Verordnungsmöglichkeiten nach Befund", margin, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    info.alternativen.forEach((item) => {
      const lines = doc.splitTextToSize(`•  ${normalizeText(item)}`, pageWidth - margin * 2 - 4);
      checkY(lines.length * 4.5);
      doc.text(lines, margin + 2, y);
      y += lines.length * 4.5;
    });

    y += 4;
  };

  const drawRecipe = (subKey, isPair, footLabel) => {
    const recipe     = RECIPES[subKey];
    const recipeText = recipe
      ? (isPair ? recipe.pair : recipe.single) ?? "Rezeptvorschlag wird noch ergänzt."
      : "Rezeptvorschlag wird noch ergänzt.";

    checkY(footLabel ? 20 : 14);

    if (footLabel) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(normalizeText(footLabel), margin, y);
      y += 5;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(30, 139, 202);
    doc.text(normalizeText(subKey || "—"), margin, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0);
    const lines = doc.splitTextToSize(normalizeText(recipeText), pageWidth - margin * 2);
    checkY(lines.length * 5 + 4);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 4;

    drawZusatz(subKey);
  };

  const drawNoType = (footLabel) => {
    checkY(footLabel ? 15 : 10);
    if (footLabel) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text(normalizeText(footLabel), margin, y);
      y += 5;
    }
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text("Kein Risikotyp bestimmt – kein Rezeptvorschlag verfügbar.", margin, y);
    y += 8;
    doc.setTextColor(0);
  };

  if (sameSubVariant && subKeyLinks) {
    drawRecipe(subKeyLinks, true, null);
  } else if (sameSubVariant && !subKeyLinks) {
    drawNoType(showBothFeet ? "Linker Fuß" : null);
    if (showBothFeet) drawNoType("Rechter Fuß");
  } else {
    if (showLeftFoot) {
      subKeyLinks
        ? drawRecipe(subKeyLinks,  false, showBothFeet ? "Linker Fuß"  : null)
        : drawNoType(showBothFeet ? "Linker Fuß"  : null);
    }
    if (showRightFoot) {
      subKeyRechts
        ? drawRecipe(subKeyRechts, false, showBothFeet ? "Rechter Fuß" : null)
        : drawNoType(showBothFeet ? "Rechter Fuß" : null);
    }
  }

  // ── Footer on every page ─────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(80);
    doc.text(
      "Hinweis: Diese Dokumentation dient der Kommunikation und ersetzt nicht die ärztliche Beurteilung.",
      margin,
      pageHeight - 10
    );
    doc.text(`Seite ${i}`, pageWidth - margin, pageHeight - 10, { align: "right" });
  }

  // ── Download ─────────────────────────────────────────────────────────
  const pdfBlob = doc.output("blob");
  const pdfUrl  = URL.createObjectURL(pdfBlob);

  const safeName = [general.name, general.vorname]
    .filter(Boolean)
    .join("_")
    .normalize("NFC")
    .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, "_");
  const currentDate = new Date().toISOString().slice(0, 10);
  const fileName = `Rezeptvorschlag${safeName ? `_${safeName}` : ""}_${currentDate}.pdf`;

  const a = document.createElement("a");
  a.href = pdfUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
}
